/**
 * Small lookup tables shared by the UI primitives — keeps color /
 * variant mappings in one place so Button, Slider, Badge, etc. all
 * agree on what "green" means.
 *
 * See DESIGN.md §2 for the canonical palette.
 */

export type AccentColor = 'green' | 'cyan' | 'amber' | 'red' | 'purple' | 'muted';

export interface AccentTokens {
  /** Solid accent (used for borders, text-on-dark). */
  solid: string;
  /** Gradient start (35% opacity of solid, used for slider fill beginnings). */
  gradientStart: string;
  /** Subtle tinted background for pills / badges. */
  bgTint: string;
  /** Matching tinted border. */
  borderTint: string;
  /** Subtle outer glow for active states (sparingly, DESIGN.md §6). */
  glow: string;
}

export const ACCENT_TOKENS: Record<AccentColor, AccentTokens> = {
  green: {
    solid: 'var(--accent-green)',
    gradientStart: 'rgba(112, 148, 36, 0.35)',
    bgTint: 'rgba(112, 148, 36, 0.12)',
    borderTint: 'rgba(112, 148, 36, 0.45)',
    glow: '0 0 20px rgba(112, 148, 36, 0.18)',
  },
  cyan: {
    solid: 'var(--accent-cyan)',
    gradientStart: 'rgba(105, 162, 176, 0.35)',
    bgTint: 'rgba(105, 162, 176, 0.12)',
    borderTint: 'rgba(105, 162, 176, 0.45)',
    glow: '0 0 20px rgba(105, 162, 176, 0.18)',
  },
  amber: {
    solid: 'var(--accent-amber)',
    gradientStart: 'rgba(212, 160, 23, 0.35)',
    bgTint: 'rgba(212, 160, 23, 0.12)',
    borderTint: 'rgba(212, 160, 23, 0.45)',
    glow: '0 0 20px rgba(212, 160, 23, 0.18)',
  },
  red: {
    solid: 'var(--accent-red)',
    gradientStart: 'rgba(232, 93, 80, 0.35)',
    bgTint: 'rgba(232, 93, 80, 0.12)',
    borderTint: 'rgba(232, 93, 80, 0.45)',
    glow: '0 0 20px rgba(232, 93, 80, 0.18)',
  },
  purple: {
    solid: 'var(--accent-purple)',
    gradientStart: 'rgba(147, 51, 234, 0.35)',
    bgTint: 'rgba(147, 51, 234, 0.12)',
    borderTint: 'rgba(147, 51, 234, 0.45)',
    glow: '0 0 20px rgba(147, 51, 234, 0.18)',
  },
  muted: {
    solid: 'var(--text-muted)',
    gradientStart: 'rgba(138, 155, 168, 0.25)',
    bgTint: 'rgba(255, 255, 255, 0.04)',
    borderTint: 'var(--border-mid)',
    glow: 'none',
  },
};

/**
 * Common transition used throughout the UI primitives. 200–400ms
 * ease-out per DESIGN.md §7.
 */
export const UI_EASING = [0.25, 0.1, 0.25, 1] as const; // cubic-bezier ≈ ease-out
